/**
 * A custom element that formats rte content for easier styling.
 */
const richTextContainerSelector = [
  'rte-formatter',
  '.rte',
  '.text-block',
  '.desktop-product-description',
  '.mobile-product-description',
  '.product-description',
  '.product__description',
  '.short-description-text',
].join(',');

const richTextImageSelector = [
  'rte-formatter img',
  '.rte img',
  '.text-block img',
  '.desktop-product-description img',
  '.mobile-product-description img',
  '.product-description img',
  '.product__description img',
  '.short-description-text img',
].join(',');

function isRichTextContainer(element) {
  return element instanceof Element && element.matches(richTextContainerSelector);
}

/**
 * Adds default lazy loading attributes to rich text images.
 * @param {HTMLImageElement} image
 */
function formatRichTextImage(image) {
  image.setAttribute('loading', 'lazy');
  image.setAttribute('decoding', 'async');
}

/**
 * Adds image lazy loading attributes before rich text HTML is rendered.
 * @param {string} html
 * @returns {string}
 */
function formatRichTextHtml(html) {
  if (typeof html !== 'string' || !html.includes('<img')) return html;

  const template = document.createElement('template');
  template.innerHTML = html;
  template.content.querySelectorAll('img').forEach(formatRichTextImage);

  return template.innerHTML;
}

/**
 * Formats rich text images in a DOM scope.
 * @param {Document | DocumentFragment | Element} scope
 */
function formatRichTextImages(scope = document) {
  if (scope instanceof HTMLImageElement && scope.matches(richTextImageSelector)) {
    formatRichTextImage(scope);
    return;
  }

  scope.querySelectorAll?.(richTextImageSelector).forEach(formatRichTextImage);
}

function patchRichTextInnerHTML() {
  if (Element.prototype.__enhulkRichTextInnerHTMLPatched) return;

  const descriptor = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
  if (!descriptor?.get || !descriptor?.set) return;

  Object.defineProperty(Element.prototype, '__enhulkRichTextInnerHTMLPatched', {
    value: true,
  });

  Object.defineProperty(Element.prototype, 'innerHTML', {
    configurable: descriptor.configurable,
    enumerable: descriptor.enumerable,
    get() {
      return descriptor.get.call(this);
    },
    set(value) {
      descriptor.set.call(this, isRichTextContainer(this) ? formatRichTextHtml(value) : value);
    },
  });
}

function initRichTextFormatting() {
  patchRichTextInnerHTML();
  formatRichTextImages();

  window.addEventListener('load', () => formatRichTextImages(), { once: true });
  document.addEventListener('shopify:section:load', (event) => formatRichTextImages(event.target));
  document.addEventListener('variant:update', () => formatRichTextImages());
}

window.__enhulkFormatRichTextImages = formatRichTextImages;
window.__enhulkFormatRichTextHtml = formatRichTextHtml;

class RTEFormatter extends HTMLElement {
  connectedCallback() {
    formatRichTextImages(this);
    this.querySelectorAll('table').forEach(this.#formatTable);
  }

  /**
   * Formats a table for easier styling.
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRichTextFormatting, { once: true });
} else {
  initRichTextFormatting();
}
